import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import { Zap, Wrench, BarChart3, MessageCircle } from 'lucide-react';
import { ToastContainer } from '../common/ToastContainer';

export interface VisualFeature {
  icon?: ReactNode;
  text: string;
}

export interface VisualBadge {
  title: string;
  sub: string;
}

export interface AuthLayoutConfig {
  visualTagline?: string;
  visualFeatures?: VisualFeature[];
  visualBadge?: VisualBadge;
}

interface AuthLayoutContextType {
  setConfig: (config: AuthLayoutConfig) => void;
}

const AuthLayoutContext = createContext<AuthLayoutContextType>({ setConfig: () => {} });

export const useAuthLayoutConfig = () => useContext(AuthLayoutContext);

const DEFAULT_TAGLINE = 'チャットBOTが、3分で作れる。\nあなたのサイトにAIアシスタントを。';

const DEFAULT_FEATURES: VisualFeature[] = [
  { icon: <Zap className="h-3.5 w-3.5" />, text: 'URLを入力するだけで自動学習' },
  { icon: <Wrench className="h-3.5 w-3.5" />, text: 'コード1行でサイトに設置' },
  { icon: <BarChart3 className="h-3.5 w-3.5" />, text: 'チャット分析で改善提案' },
];

export const AuthLayout: React.FC = () => {
  const [config, setConfigState] = useState<AuthLayoutConfig>({});

  const setConfig = React.useCallback((cfg: AuthLayoutConfig) => setConfigState(cfg), []);
  const contextValue = React.useMemo(() => ({ setConfig }), [setConfig]);

  const visualTagline = config.visualTagline ?? DEFAULT_TAGLINE;
  const visualFeatures = config.visualFeatures ?? DEFAULT_FEATURES;
  const visualBadge = config.visualBadge;

  const taglineLines = visualTagline.split('\n');

  return (
    <AuthLayoutContext.Provider value={contextValue}>
      <div
        data-testid="auth-layout"
        className="min-h-screen flex flex-col md:flex-row bg-[#F8FAFC]"
      >
        {/* Mobile header (md以下のみ表示) */}
        <div
          className="md:hidden flex-shrink-0 flex flex-col items-center justify-center py-8 px-6 text-white"
          style={{ background: 'linear-gradient(135deg, #0a7a72, #14B8A6)' }}
        >
          <div className="flex items-center gap-2 text-2xl font-bold mb-2">
            <div
              className="flex items-center justify-center rounded-[10px]"
              style={{ width: 36, height: 36, background: 'rgba(255,255,255,0.2)' }}
            >
              <MessageCircle className="h-5 w-5" />
            </div>
            EntryBOT
          </div>
          <p className="text-sm opacity-90 text-center">{taglineLines[0]}</p>
        </div>

        {/* Desktop left panel (md以上のみ表示) */}
        <div
          className="hidden md:flex w-1/2 flex-col items-center justify-center p-[60px] relative overflow-hidden text-white"
          style={{ background: 'linear-gradient(135deg, #0a7a72, #14B8A6)' }}
        >
          {/* 装飾円 (大) */}
          <div
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 500,
              height: 500,
              background: 'rgba(255,255,255,0.05)',
              top: -100,
              right: -100,
            }}
          />
          {/* 装飾円 (小) */}
          <div
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 300,
              height: 300,
              background: 'rgba(255,255,255,0.05)',
              bottom: -50,
              left: -50,
            }}
          />

          {/* ロゴ */}
          <div className="relative z-10 flex items-center gap-3 mb-4 font-bold" style={{ fontSize: 32 }}>
            <div
              className="flex items-center justify-center rounded-[14px]"
              style={{ width: 48, height: 48, background: 'rgba(255,255,255,0.2)' }}
            >
              <MessageCircle className="h-6 w-6" />
            </div>
            EntryBOT
          </div>

          {/* タグライン */}
          <p className="relative z-10 opacity-90 text-center leading-relaxed" style={{ fontSize: 18 }}>
            {taglineLines.map((line, i) => (
              <React.Fragment key={i}>
                {line}
                {i < taglineLines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>

          {/* サインアップ: バッジ + チェックリスト */}
          {visualBadge ? (
            <>
              <div
                className="relative z-10 mt-8 rounded-xl px-6 py-4 text-center"
                style={{ background: 'rgba(255,255,255,0.15)' }}
              >
                <div className="text-xl font-bold mb-1">{visualBadge.title}</div>
                <div className="text-sm" style={{ opacity: 0.85 }}>{visualBadge.sub}</div>
              </div>
              <div className="relative z-10 mt-5 flex flex-col gap-2.5 self-start">
                {visualFeatures.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm" style={{ opacity: 0.85 }}>
                    {f.icon && (
                      <span style={{ color: '#A7F3D0', flexShrink: 0 }}>{f.icon}</span>
                    )}
                    <span>{f.text}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            /* デフォルト: アイコン付きフィーチャーリスト */
            <div className="relative z-10 mt-10 flex flex-col gap-4">
              {visualFeatures.map((f, i) => (
                <div key={i} className="flex items-center gap-3 text-[15px]" style={{ opacity: 0.85 }}>
                  <div
                    className="flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ width: 32, height: 32, background: 'rgba(255,255,255,0.15)' }}
                  >
                    {f.icon}
                  </div>
                  <span>{f.text}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 右パネル (フォームエリア) */}
        <div className="flex-1 md:w-1/2 flex items-center justify-center py-8 px-6 md:p-[60px]">
          <div className="w-full max-w-[400px]">
            <Outlet />
          </div>
        </div>
      </div>

      <ToastContainer />
    </AuthLayoutContext.Provider>
  );
};
